/**
 * ManualDutchEntryModal.jsx — iter790 (Feb 2026)
 *
 * Alternative to SpecialistLabUpload for patients/doctors who have DUTCH
 * values on paper (напр. лабораторни бележки) но нямат PDF. Изпраща
 * POST /api/bhi/specialist-lab/manual-dutch — записва в същата
 * db.specialist_labs колекция със ``test_type='dutch_manual'``.
 *
 * Всяко поле е ОПЦИОНАЛНО — сървърът приема поне един метаболит
 * или cortisol_pattern.
 */
import React, { useState } from "react";
import axios from "axios";
import { X, Loader2, FlaskConical, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { emitHealthEvent } from "@/utils/healthEvents";

const FIELDS = [
  // key                       label                                    unit         step
  ["estradiol_e2_ng_mg",       "Естрадиол (E2)",                        "ng/mg",     0.1],
  ["estrone_e1_ng_mg",         "Естрон (E1)",                           "ng/mg",     0.1],
  ["estriol_e3_ng_mg",         "Естриол (E3)",                          "ng/mg",     0.1],
  ["2_oh_E1_ng_mg",            "2-OH-E1",                               "ng/mg",     0.1],
  ["16_oh_E1_ng_mg",           "16-OH-E1",                              "ng/mg",     0.1],
  ["4_oh_E1_ng_mg",            "4-OH-E1",                               "ng/mg",     0.05],
  ["2_meo_E1_ng_mg",           "2-MeO-E1 (COMT продукт)",                "ng/mg",     0.1],
  ["ratio_2_OH_to_16_OH",      "2:16-OH съотношение",                    "",          0.1],
  ["methylation_efficiency_pct","Ефективност на метилиране",             "%",         1],
];

const CORTISOL_OPTIONS = [
  ["",             "— не е известен —"],
  ["normal",       "Нормален диурнален ритъм"],
  ["flat",         "Плосък ритъм"],
  ["blunted",      "Притъпен ритъм"],
  ["elevated_pm",  "Повишен вечерен кортизол"],
  ["elevated_am",  "Повишен сутрешен кортизол"],
];

export default function ManualDutchEntryModal({ open, onClose, onSaved }) {
  const { API } = useAuth();
  const [values, setValues] = useState({});
  const [cortisolPattern, setCortisolPattern] = useState("");
  const [collectionDate, setCollectionDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const setField = (k, v) => {
    setValues((prev) => ({ ...prev, [k]: v }));
  };

  const filledCount =
    Object.values(values).filter((v) => v !== "" && v !== null && v !== undefined).length +
    (cortisolPattern ? 1 : 0);

  const submit = async () => {
    if (filledCount === 0) {
      setError("Въведете поне една стойност или cortisol pattern.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = { collection_date: collectionDate || null };
      for (const [k, v] of Object.entries(values)) {
        if (v === "" || v === null || v === undefined) continue;
        const n = Number(v);
        if (!Number.isFinite(n) || n <= 0) continue;
        body[k] = n;
      }
      if (cortisolPattern) body.cortisol_pattern = cortisolPattern;

      const { data } = await axios.post(
        `${API}/bhi/specialist-lab/manual-dutch`,
        body,
        { timeout: 15000 },
      );
      toast.success("DUTCH стойностите са записани. BHI и Twin-ите ще ги отчетат.");
      emitHealthEvent("conditions-changed", { source: "dutch_manual", flags: data?.flags });
      onSaved?.(data);
      // Reset + close
      setValues({});
      setCortisolPattern("");
      setCollectionDate("");
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Грешка при запис.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="manual-dutch-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-slate-900 dark:text-slate-100 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-purple-500" />
            Ръчно въвеждане на DUTCH стойности
          </h3>
          <button
            onClick={onClose}
            data-testid="manual-dutch-close"
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-white/10 rounded-lg"
            aria-label="Затвори"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <p className="text-[12px] text-slate-600">
            Ако имате отделни стойности от лабораторни бележки (без PDF) — въведете тук.
            <b> Всяко поле е опционално;</b> запишете само това, което знаете. Ще запазим същата
            структура като AI-parsed DUTCH panel и ще преизчислим BHI + Twin-ите.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="col-span-full">
              <label className="text-[11px] font-semibold text-slate-600 block mb-1">Дата на пробата (опция)</label>
              <input
                type="date"
                value={collectionDate}
                onChange={(e) => setCollectionDate(e.target.value)}
                data-testid="manual-dutch-date"
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
              />
            </div>

            {FIELDS.map(([key, label, unit, step]) => (
              <div key={key}>
                <label className="text-[11px] font-semibold text-slate-600 block mb-1">
                  {label} {unit && <span className="text-slate-400">({unit})</span>}
                </label>
                <input
                  type="number"
                  step={step}
                  min="0"
                  value={values[key] ?? ""}
                  onChange={(e) => setField(key, e.target.value)}
                  placeholder="—"
                  data-testid={`manual-dutch-${key}`}
                  className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
                />
              </div>
            ))}

            <div className="col-span-full">
              <label className="text-[11px] font-semibold text-slate-600 block mb-1">Кортизолен диурнален ритъм</label>
              <select
                value={cortisolPattern}
                onChange={(e) => setCortisolPattern(e.target.value)}
                data-testid="manual-dutch-cortisol"
                className="w-full px-2 py-1.5 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800"
              >
                {CORTISOL_OPTIONS.map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-[11px] text-slate-500 bg-slate-50 rounded-lg p-2.5">
            <b>Съвет:</b> Ако въведете 2-OH-E1 и 16-OH-E1 без съотношението, ще го изчислим автоматично.
            За целите на BHI флаговете, стойности &lt; 60% за метилиране и &gt; 0.5 ng/mg за 4-OH-E1 се
            третират като рискови.
          </div>

          {error && (
            <div className="text-[12px] text-rose-600 bg-rose-50 rounded-lg p-2" data-testid="manual-dutch-error">
              {error}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 px-5 py-3 flex items-center justify-between rounded-b-2xl">
          <div className="text-[11px] text-slate-500">
            {filledCount > 0 ? (
              <span className="text-emerald-700 font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {filledCount} стойност{filledCount === 1 ? "" : "и"} готова(и) за запис
              </span>
            ) : (
              "Въведете поне една стойност"
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-[12px] font-semibold text-slate-600 hover:bg-slate-100"
            >
              Отказ
            </button>
            <button
              onClick={submit}
              disabled={saving || filledCount === 0}
              data-testid="manual-dutch-save"
              className={`px-4 py-1.5 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 ${
                saving || filledCount === 0
                  ? "bg-slate-300 cursor-not-allowed"
                  : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              }`}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FlaskConical className="w-3.5 h-3.5" />}
              Запази DUTCH стойностите
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
